/**
 * Contador de saltos entre aplicaciones, para que una discrepancia entre la web y el back office no
 * vuelva a dejar a nadie dando vueltas.
 *
 * Existe como red de seguridad, no como arreglo: la causa la quita `ensureFreshSession`. Pero los
 * dos despachadores por rol siguen siendo dos trozos de codigo en dos repos, y el dia que vuelvan a
 * discrepar por un motivo que hoy no se nos ocurre, el usuario tiene que acabar en una pantalla con
 * un boton y no en un ciclo del que ni Atras le saca.
 *
 * En `sessionStorage` y no en `localStorage` a proposito: el bucle es de una pestana, y una pestana
 * atascada no tiene por que cortarle las redirecciones legitimas a otra. En local las dos apps
 * estan en puertos distintos y cada origen lleva su cuenta; da igual, el ciclo pasa por los dos y
 * cualquiera de los dos contadores llega al limite. En prepro y prod comparten origen y la cuenta
 * es una sola.
 */
const STORAGE_KEY = 'j2l_role_redirect_hops';

/**
 * Ventana en la que se consideran encadenados dos saltos.
 *
 * Un minuto y no quince segundos porque cada salto es una carga en frio completa de una SPA mas la
 * comprobacion de sesion: en el bucle que se reprodujo salian unos 3,5 s por vuelta, y contando cada
 * origen por separado la ventana corta podaba las marcas antes de que el contador llegase al limite.
 * La red de seguridad que nunca salta no es una red de seguridad.
 */
const WINDOW_MS = 60000;

/** Saltos permitidos dentro de la ventana. Un viaje legitimo son uno o dos. */
const MAX_HOPS = 4;

const readHops = (): number[] => {
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    const parsed = raw == null ? [] : JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((value) => typeof value === 'number') : [];
  } catch {
    return [];
  }
};

/**
 * Apunta un salto entre aplicaciones y dice si se puede seguir.
 *
 * Devuelve `false` cuando se han encadenado demasiados en muy poco tiempo: eso es un bucle, y quien
 * llama debe parar y ensenar una salida en vez de redirigir. Si `sessionStorage` no esta disponible
 * (modo privado, cookies bloqueadas) devuelve `true`: bloquear una navegacion legitima por no poder
 * escribir seria peor que el problema que esto resuelve.
 */
export const recordRoleRedirect = (now: number = Date.now()): boolean => {
  // Se recorta al escribir: un llamante que ignore el `false` no puede engordar el array sin techo.
  const hops = [...readHops().filter((timestamp) => now - timestamp < WINDOW_MS), now].slice(-(MAX_HOPS + 1));

  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(hops));
  } catch {
    return true;
  }

  return hops.length <= MAX_HOPS;
};

/** Se llama al aterrizar donde toca: el viaje termino bien y la cuenta vuelve a cero. */
export const clearRoleRedirects = (): void => {
  try {
    window.sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // Sin almacen no hay nada que limpiar.
  }
};
